import React, { useRef, useState } from 'react';
import { useMutation, gql } from '@apollo/client';
import { withTranslation } from 'react-i18next';

import {
  Button,
  Grid,
  TextField,
  DialogActions,
  Alert,
  Snackbar,
  Container,
  Box,
  Typography,
  Chip,
} from '@mui/material';
import useGlobalStyles from '../utils/globalStyles';
import { UpgradeCustomerToNestPassMembers } from '../apollo';

const UPGRADE_CUSTOMER_TO_NESTPASS= gql`
${UpgradeCustomerToNestPassMembers}
`

function UpgradeToNestPass(props) {
  const globalClasses = useGlobalStyles();
  const { t } = props;
  const formRef = useRef();
  const [error, errorSetter] = useState('');
  const [success, successSetter] = useState('');
  const [openSnackk, setOpenSnackk] = useState(false);
  const [validationResults, setValidationResults] = useState([]);
  const [formData, setFormData] = useState({
    phoneNumbers: '',
    expiryDate: '',
    message:'',
  });

  const phoneRegex = /^\+91\d{10}$/;
  const totalEntries = validationResults.length;
  const validCount = validationResults.filter((item) => item.status === 'valid').length;
  const duplicateCount = validationResults.filter((item) => item.status === 'duplicate').length;
  const invalidCount = validationResults.filter((item) => item.status === 'invalid').length;
  const MAX_PHONE_ENTRIES = 50;
  const DISPLAY_LIMIT = 50;

  const parsePhoneEntries = (phoneNumbers) => {
    return phoneNumbers
      .split(/[\n,]+/)
      .map((num) => num.trim())
      .filter((num) => num.length > 0);
  };

  const buildValidationResults = (phoneNumbers) => {
    const entries = parsePhoneEntries(phoneNumbers);
    const seen = new Set();

    return entries.map((entry, index) => {
      const isDuplicate = seen.has(entry);
      seen.add(entry);
      const isValidFormat = phoneRegex.test(entry);

      let status = 'valid';
      let message = '';

      if (!isValidFormat) {
        status = 'invalid';
        message = t('Use the format +91XXXXXXXXXX.');
      } else if (isDuplicate) {
        status = 'duplicate';
        message = t('Duplicate phone number.');
      }

      return { value: entry, status, message, index: index + 1 };
    });
  };

  const handleFormInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));

    if (name === 'phoneNumbers') {
      setValidationResults(buildValidationResults(value));
    }
  };

  const validateInputs = () => {
    const { expiryDate, phoneNumbers } = formData;
    const currentDate = new Date().toISOString().split('T')[0];
    const entries = parsePhoneEntries(phoneNumbers);

    if (expiryDate && expiryDate < currentDate) {
      errorSetter(t('Expiry date cannot be in the past.'));
      setOpenSnackk(true);
      return false;
    }

    if (entries.length > MAX_PHONE_ENTRIES) {
      errorSetter(
        t('You can add up to {{max}} phone numbers at a time. Please split the list into smaller batches.', {
          max: MAX_PHONE_ENTRIES,
        })
      );
      setOpenSnackk(true);
      return false;
    }

    const results = buildValidationResults(phoneNumbers);
    setValidationResults(results);

    const failedEntries = results.filter((item) => item.status !== 'valid');

    if (!phoneNumbers.trim()) {
      errorSetter(t('Please enter at least one phone number.'));
      setOpenSnackk(true);
      return false;
    }

    if (failedEntries.length > 0) {
      const failedNumbers = failedEntries
        .map((item) => `${item.value} (${item.message})`)
        .join(', ');
      errorSetter(t('Please fix the highlighted phone numbers. {{details}}', { details: failedNumbers }));
      setOpenSnackk(true);
      return false;
    }

    return true;
  };

  const onCompleted = () => {
    const processedPhones = parsePhoneEntries(formData.phoneNumbers);
    const message = processedPhones.length > 0
      ? t('Successfully processed {{count}} number(s): {{numbers}}', {
          count: processedPhones.length,
          numbers: processedPhones.join(', '),
        })
      : t('Customers upgraded to NestPass successfully');
    errorSetter('');
    successSetter(message);
    setTimeout(hideAlert, 5000);
    setOpenSnackk(true);
    setFormData({ phoneNumbers: '', expiryDate: '', message: '' });
    setValidationResults([]);
  };

  const onError = ({ graphQLErrors, networkError }) => {
    successSetter('')

    if (graphQLErrors && graphQLErrors.length > 0) {
      errorSetter(graphQLErrors[0].message || 'An error occurred')
    } else if (
      networkError &&
      networkError.result &&
      networkError.result.errors &&
      networkError.result.errors.length > 0
    ) {
      errorSetter(
        networkError.result.errors[0].message || 'A network error occurred'
      )
    } else {
      errorSetter('Something went wrong!')
    }

    setOpenSnackk(true)
    setTimeout(hideAlert, 3000)
  }

  const [mutate, { loading: mutateLoading }] = useMutation(UPGRADE_CUSTOMER_TO_NESTPASS,{
    onError,
    onCompleted,
  });

  const hideAlert = () => {
    errorSetter('');
    successSetter('');
    setOpenSnackk(false);
  };

  const visibleValidationResults = validationResults.slice(0, DISPLAY_LIMIT);
  const hiddenEntriesCount = Math.max(0, validationResults.length - DISPLAY_LIMIT);

  const handleClear = () => {
    setFormData({ phoneNumbers: '', expiryDate: '', message: '' });
    setValidationResults([]);
    errorSetter('');
    successSetter('');
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!validateInputs()) {
      return;
    }

    const phones = buildValidationResults(formData.phoneNumbers)
      .filter((item) => item.status === 'valid')
      .map((item) => item.value);

    mutate({
      variables: {
        phones,
      },
    });
  };
  return (
     <>
       <Container className={globalClasses.flexRow}>
         <Grid
           sx={{
             backgroundColor: 'white',
             py: 8,
             px: 8,
             margin: '1.5rem',
             borderRadius: '10px',
             boxShadow: '0 4px 10px rgba(0, 0, 0, 0.1)',
           }}
         >
           <form ref={formRef} onSubmit={onSubmit}>
             <Box
               sx={{
                 mb: 3,
                 p: 2.5,
                 borderRadius: '10px',
                 background: 'linear-gradient(135deg, #f8fbff 0%, #f3f7ff 100%)',
                 border: '1px solid #e5ecf7',
               }}
             >
               <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937', mb: 0.5 }}>
                 {t('Upgrade Customers to NestPass')}
               </Typography>
               <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                 {t('Enter phone numbers in bulk. Each number must start with +91 and contain 10 digits after the country code.')}
               </Typography>
             </Box>
             <Grid container spacing={3}>
              
 
               <Grid item xs={12}>
                 <Grid container alignItems="top">
                   <Grid item xs={2}>
                     <Typography variant="body1" style={{ textAlign: 'left' }}>
                       {t('Enter Customer Phone Numbers')}:
                     </Typography>
                   </Grid>
                   <Grid item xs={10}>
                     <TextField
                       name="phoneNumbers"
                       variant="outlined"
                       fullWidth
                       multiline
                       rows={12}
                       value={formData.phoneNumbers}
                       onChange={handleFormInputChange}
                       placeholder="+919731xxxxxx,+919886xxxxxxx"
                       inputProps={{ style: { color: 'black' } }}
                       helperText={t('Separate numbers with commas or new lines. Maximum 50 entries at a time.')}
                     />
                         {visibleValidationResults.some((item) => item.status !== 'valid') && (
                           <Box>
                             <Typography variant="subtitle2" sx={{ mb: 1 }}>
                               {t('Needs attention')}
                             </Typography>
                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                               {visibleValidationResults
                                 .filter((item) => item.status !== 'valid')
                                 .map((item) => (
                                   <Chip
                                     key={`${item.value}-${item.index}`}
                                     label={`${item.value} (${item.message})`}
                                     color={item.status === 'duplicate' ? 'warning' : 'error'}
                                     variant="filled"
                                   />
                                 ))}
                             </Box>
                           </Box>
                         )}
                     {validationResults.length > 0 && (
                       <Box mt={2} sx={{ p: 2, borderRadius: '8px', backgroundColor: '#fafcff', border: '1px solid #e5ecf7' }}>
                         <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
                           {t('Summary')}
                         </Typography>
                         <Grid container spacing={1.5}>
                           <Grid item xs={12} sm={4}>
                             <Box sx={{ p: 1.5, borderRadius: '8px', backgroundColor: '#eaf7ee' }}>
                               <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600 }}>
                                 {t('Valid')}
                               </Typography>
                               <Typography variant="h6" sx={{ color: 'success.main', mt: 0.5 }}>
                                 {validCount}
                               </Typography>
                             </Box>
                           </Grid>
                           <Grid item xs={12} sm={4}>
                             <Box sx={{ p: 1.5, borderRadius: '8px', backgroundColor: '#fff8e1' }}>
                               <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 600 }}>
                                 {t('Duplicates')}
                               </Typography>
                               <Typography variant="h6" sx={{ color: 'warning.main', mt: 0.5 }}>
                                 {duplicateCount}
                               </Typography>
                             </Box>
                           </Grid>
                           <Grid item xs={12} sm={4}>
                             <Box sx={{ p: 1.5, borderRadius: '8px', backgroundColor: '#fdecea' }}>
                               <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                                 {t('Invalid')}
                               </Typography>
                               <Typography variant="h6" sx={{ color: 'error.main', mt: 0.5 }}>
                                 {invalidCount}
                               </Typography>
                             </Box>
                           </Grid>
                         </Grid>
                       </Box>
                     )}
                     {validationResults.length > 0 && (
                       <Box mt={2}>
                         {hiddenEntriesCount > 0 && (
                           <Alert severity="info" sx={{ mb: 1.5 }}>
                             {t('Showing the first {{limit}} entries of {{total}}. Please split larger batches into smaller groups.', {
                               limit: DISPLAY_LIMIT,
                               total: validationResults.length,
                             })}
                           </Alert>
                         )}
                     {/*   {visibleValidationResults.some((item) => item.status === 'valid') && (
                           <Box mb={1.5}>
                             <Typography variant="subtitle2" sx={{ mb: 1 }}>
                               {t('Valid numbers')}
                             </Typography>
                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                               {visibleValidationResults
                                 .filter((item) => item.status === 'valid')
                                 .map((item) => (
                                   <Chip
                                     key={`${item.value}-${item.index}`}
                                     label={item.value}
                                     color="success"
                                     variant="outlined"
                                   />
                                 ))}
                             </Box>
                           </Box>
                         )}*/}
                       </Box>
                     )}
                   </Grid>
                 </Grid>
               </Grid>
             </Grid>
 
             <DialogActions sx={{ pt: 2, px: 0 }}>
               <Button
                 type="button"
                 onClick={handleClear}
                 sx={{
                   color: '#5f6368',
                   border: '1px solid #d0d7de',
                   borderRadius: '8px',
                   mr: 1,
                 }}
               >
                 {t('Clear')}
               </Button>
               <Button
                 type="submit"
                 className={globalClasses.dashbordBtn}
                 disabled={mutateLoading}
                 sx={{
                   backgroundColor: '#3f51b5',
                   color: 'white',
                   '&:hover': {
                     backgroundColor: '#303f9f',
                   },
                 }}
               >
                 {t('Upgrade')}
               </Button>
             </DialogActions>
           </form>
         </Grid>
       </Container>
       <Box mt={2}>
         {success && (
           <Snackbar
             open={openSnackk}
             autoHideDuration={3000}
             anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
             onClose={hideAlert}
           >
             <Alert onClose={hideAlert} severity="success" variant="filled" sx={{ width: '100%' }}>
               {success}
             </Alert>
           </Snackbar>
         )}
         {error && (
           <Snackbar
             open={openSnackk}
             autoHideDuration={3000}
             anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
             onClose={hideAlert}
           >
             <Alert onClose={hideAlert} severity="error" variant="filled" sx={{ width: '100%' }}>
               {error}
             </Alert>
           </Snackbar>
         )}
       </Box>
     </>
   );
 }

export default withTranslation()(UpgradeToNestPass);
